/**
 * useGoalKbProvisioning — the React binding that owns the Goal knowledge base
 * PROVISIONING state machine.
 *
 * Strict layering: pages call THIS hook; the hook calls {@link GoalKnowledgeStore}
 * (StartKnowledgeBaseProvisioning + DescribeExecution); the store calls the api
 * facade. Pages never import the store or the api directly.
 *
 * Flow (Phase 5):
 *  1. On load (autoStart) — idempotently START provisioning the Goal KB
 *     (sectionKey "goal"). If the backend reports `alreadyProvisioned`, the KB
 *     is READY immediately (no workflow).
 *  2. Otherwise POLL DescribeExecution every 5s on the returned executionArn,
 *     surfacing a friendly per-step label while the SFN execution is RUNNING,
 *     until it reaches SUCCEEDED (ready) or a failure status (error).
 *  3. On failure, {@link UseGoalKbProvisioningResult.start} doubles as RETRY —
 *     it re-calls StartKnowledgeBaseProvisioning and resumes polling.
 *
 * The timer is torn down on unmount and whenever provisioning is (re)started,
 * and stale polls (from a previous execution) are ignored, so overlapping runs
 * never clobber state.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { GoalKnowledgeStore } from './goalKnowledgeStore';
import { goalKbProvisioningPhase, goalKbStepLabel } from './goalKnowledge';

/** Poll interval for DescribeExecution while the workflow is RUNNING. */
export const GOAL_KB_POLL_INTERVAL_MS = 5000;

/** The Goal KB provisioning UI state. */
export type GoalKbProvisioningState =
  | 'idle'
  | 'starting'
  | 'running'
  | 'ready'
  | 'error';

export interface UseGoalKbProvisioningResult {
  /** The current provisioning state. */
  state: GoalKbProvisioningState;
  /** Friendly current-step label shown while RUNNING. */
  step: string;
  /** Raw Step Functions execution status, once known. */
  status: string | null;
  /** The resolved knowledge base id when ready, or null. */
  knowledgeBaseId: string | null;
  /** Error message when {@link state} is 'error', or null. */
  error: string | null;
  /** True when the KB was already provisioned (ready without a workflow). */
  alreadyProvisioned: boolean;
  /** Starts provisioning — also used as RETRY after a failure. */
  start: () => Promise<void>;
}

export function useGoalKbProvisioning(
  options: { autoStart?: boolean } = {},
): UseGoalKbProvisioningResult {
  const { autoStart = true } = options;
  const api = useApi();
  const store = useMemo(() => new GoalKnowledgeStore(api), [api]);

  const [state, setState] = useState<GoalKbProvisioningState>('idle');
  const [step, setStep] = useState<string>('');
  const [status, setStatus] = useState<string | null>(null);
  const [knowledgeBaseId, setKnowledgeBaseId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [alreadyProvisioned, setAlreadyProvisioned] = useState(false);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);
  const pollingArnRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const pollOnce = useCallback(
    async (executionArn: string) => {
      try {
        const desc = await store.describeProvisioning(executionArn);
        // Ignore stale polls (unmounted, or a newer execution superseded this).
        if (!mountedRef.current || pollingArnRef.current !== executionArn) {
          return;
        }
        setStatus(desc.status);
        const phase = goalKbProvisioningPhase(desc.status);
        if (phase === 'running') {
          setStep(goalKbStepLabel(desc.output));
          return;
        }
        clearTimer();
        pollingArnRef.current = null;
        if (phase === 'ready') {
          setState('ready');
        } else {
          setState('error');
          setError('Goal knowledge base provisioning failed.');
        }
      } catch (e) {
        if (!mountedRef.current || pollingArnRef.current !== executionArn) {
          return;
        }
        clearTimer();
        pollingArnRef.current = null;
        setState('error');
        setError(
          e instanceof Error
            ? e.message
            : 'Failed to check provisioning status.',
        );
      }
    },
    [store, clearTimer],
  );

  const start = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    clearTimer();
    pollingArnRef.current = null;
    setState('starting');
    setError(null);
    setStep('');
    setStatus(null);
    try {
      const result = await store.startProvisioning();
      if (!mountedRef.current) return;
      setKnowledgeBaseId(result.knowledgeBaseId || null);
      if (result.alreadyProvisioned || !result.executionArn) {
        setAlreadyProvisioned(result.alreadyProvisioned);
        setState('ready');
        return;
      }
      setAlreadyProvisioned(false);
      setState('running');
      setStep(goalKbStepLabel());
      const arn = result.executionArn;
      pollingArnRef.current = arn;
      // Poll immediately, then every 5s until terminal.
      void pollOnce(arn);
      timerRef.current = setInterval(
        () => void pollOnce(arn),
        GOAL_KB_POLL_INTERVAL_MS,
      );
    } catch (e) {
      if (!mountedRef.current) return;
      setState('error');
      setError(
        e instanceof Error ? e.message : 'Failed to start provisioning.',
      );
    } finally {
      inFlightRef.current = false;
    }
  }, [store, clearTimer, pollOnce]);

  const autoStartedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    if (autoStart && !autoStartedRef.current) {
      autoStartedRef.current = true;
      void start();
    }
    return () => {
      mountedRef.current = false;
      clearTimer();
    };
  }, [autoStart, start, clearTimer]);

  return {
    state,
    step,
    status,
    knowledgeBaseId,
    error,
    alreadyProvisioned,
    start,
  };
}
